import {
    Paper,
    styled,
    Stack,
    Box,
    TextField,
    IconButton,
    Switch,
    FormControlLabel,
    InputAdornment,
    Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ShareIcon from '@mui/icons-material/Share';
import ViewListIcon from '@mui/icons-material/ViewList';
import React, { useState, useEffect } from 'react';
import PLSummaryCard from '../../../components/PLSummaryCard';
import BrokerDataTable from '../../../components/BrokerDataTable';
import useAuth from '../../../hooks/useAuth';
// Format number with Indian currency format
const formatCurrency = (number) => {
    // If number is already a string with ₹ symbol, return it as is
    if (typeof number === 'string' && number.startsWith('₹')) {
        return number;
    }    // Convert to number if it's a string without ₹ symbol
    const numValue = typeof number === 'string' ? parseFloat(number) : number;

    // Check if it's a valid number
    if (Number.isNaN(numValue) || numValue === undefined || numValue === null) {
        return '0';
    }

    // Convert to integer by rounding and format with thousand separators
    return Math.round(numValue).toLocaleString('en-IN');
    // return `₹${numValue.toLocaleString('en-IN')}`;
};

// Styled components - Unified design without background partitions
const UnifiedPanel = styled(Box)(({ theme }) => ({
    width: '100%',
    backgroundColor: 'transparent',
    padding: 0,
    borderRadius: 0,
    border: 'none',
    boxShadow: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    height: 'fit-content',
    minHeight: 'auto',
    [theme.breakpoints.down('md')]: {
        gap: theme.spacing(0.5),
    }
}));

// Scrollable content container with dynamic height
const ScrollableContainer = styled(Box)(({ theme, itemCount = 0 }) => {
    const shouldScroll = itemCount > 6; // Enable scrolling if more than 6 items (about 600px of content)

    return {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: 'fit-content',
        maxHeight: shouldScroll ? 'calc(100vh - 250px)' : 'none',
        overflowY: shouldScroll ? 'auto' : 'visible',
        overflowX: 'auto', // Always allow horizontal scrolling
        scrollbarWidth: 'thin',
        '&::-webkit-scrollbar': {
            width: '6px',
            height: '6px', // Also enable horizontal scrollbar
        },
        '&::-webkit-scrollbar-track': {
            backgroundColor: alpha(theme.palette.background.neutral, 0.4),
            borderRadius: '3px',
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: alpha(theme.palette.grey[500], 0.3),
            borderRadius: '3px',
            '&:hover': {
                backgroundColor: alpha(theme.palette.grey[500], 0.5),
            }
        },
        [theme.breakpoints.down('md')]: {
            maxHeight: shouldScroll ? 'calc(100vh - 200px)' : 'none',
        }
    };
});

const FilterContainer = styled(Box)(({ theme }) => ({
    position: 'sticky',
    top: 0,
    zIndex: 1100,
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    marginBottom: theme.spacing(1),
    backdropFilter: 'blur(8px)',
    borderRadius: theme.spacing(1),
    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.04)}`,
}));

const SearchTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        height: '32px',
        backgroundColor: theme.palette.background.paper,
        '& fieldset': {
            borderColor: alpha(theme.palette.grey[500], 0.2),
        },
        '&:hover fieldset': {
            borderColor: alpha(theme.palette.grey[500], 0.3),
        },
        '&.Mui-focused fieldset': {
            borderColor: theme.palette.primary.main,
        },
    },
    '& .MuiOutlinedInput-input': {
        padding: '6px 10px',
        fontSize: '0.875rem',
    },
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
    width: '32px',
    height: '32px',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
    '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.08),
    },
}));

const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
    marginRight: 0,
    marginLeft: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    '& .MuiSwitch-root': {
        width: '52px',
        height: '32px',
        padding: 0,
        borderRadius: '16px',
        border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
        backgroundColor: theme.palette.background.paper,
        '& .MuiSwitch-switchBase': {
            padding: 3,
            '&.Mui-checked': {
                transform: 'translateX(20px)',
                color: '#fff',
                '& + .MuiSwitch-track': {
                    opacity: 1,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                },
                '& .MuiSwitch-thumb': {
                    backgroundColor: '#ffffff',
                    '& .MuiSvgIcon-root': {
                        color: theme.palette.primary.main,
                        fontSize: '16px',
                        animation: 'fadeIn 0.3s ease-in-out',
                    },
                },
            },
            '&:not(.Mui-checked)': {
                '& .MuiSwitch-thumb': {
                    backgroundColor: '#ffffff',
                    '& .MuiSvgIcon-root': {
                        color: theme.palette.grey[600],
                        fontSize: '16px',
                    },
                },
            },
        },
        '& .MuiSwitch-thumb': {
            width: '24px',
            height: '24px',
            borderRadius: '12px',
            boxShadow: `0 2px 4px ${alpha(theme.palette.common.black, 0.2)}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s ease-in-out',
        },
        '& .MuiSwitch-track': {
            opacity: 1,
            backgroundColor: alpha(theme.palette.grey[500], 0.16),
            borderRadius: '16px',
            transition: 'all 0.2s ease-in-out',
        },
    },
    '@keyframes fadeIn': {
        from: {
            opacity: 0,
            transform: 'scale(0.8)',
        },
        to: {
            opacity: 1,
            transform: 'scale(1)',
        },
    },
}));

// Header row for metrics in share view
const MetricsHeaderRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    backgroundColor: alpha(theme.palette.primary.main, 0.03),
    border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(0.75, 1),
    marginBottom: theme.spacing(0.5),
    '& .header-section': {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
    },
    '& .user-section': {
        flex: '0 0 140px',
        fontWeight: 600,
        fontSize: '0.75rem',
        color: theme.palette.text.primary,
    },
    '& .metrics-section': {
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        gap: theme.spacing(2),
        [theme.breakpoints.down('lg')]: {
            display: 'none',
        },
    },
    '& .metric-header': {
        fontSize: '0.7rem',
        fontWeight: 700,
        color: theme.palette.text.secondary,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        textAlign: 'center',
        minWidth: '90px',
    },
    '& .actions-section': {
        flex: '0 0 90px',
        textAlign: 'center',
        fontSize: '0.7rem',
        fontWeight: 600,
        color: theme.palette.text.secondary,
    },
}));

// Totals row for metrics in share view
const MetricsTotalsRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    backgroundColor: alpha(theme.palette.grey[200], 0.8),
    border: `2px solid ${alpha(theme.palette.grey[400], 0.5)}`,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(0.75, 1),
    marginTop: theme.spacing(0.5),
    position: 'sticky',
    bottom: 0,
    zIndex: 5,
    backdropFilter: 'blur(8px)',
    '& .header-section': {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
    },
    '& .user-section': {
        flex: '0 0 140px',
        fontWeight: 700,
        fontSize: '0.75rem',
        color: theme.palette.text.primary,
        textTransform: 'uppercase',
        [theme.breakpoints.down('md')]: {
            flex: '0 0 80px',
            fontSize: '0.65rem',
        },
    },
    '& .metrics-section': {
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        gap: theme.spacing(2),
        [theme.breakpoints.down('lg')]: {
            gap: theme.spacing(1),
        },
        [theme.breakpoints.down('md')]: {
            gap: theme.spacing(0.5),
        },
    }, '& .metric-total': {
        fontSize: '0.7rem',
        fontWeight: 700,
        textAlign: 'center',
        minWidth: '90px',
        [theme.breakpoints.down('md')]: {
            fontSize: '0.6rem',
            minWidth: '60px',
        },
    },
    '& .actions-section': {
        flex: '0 0 120px',
        textAlign: 'center',
        fontSize: '0.7rem',
        fontWeight: 600,
        color: theme.palette.text.secondary,
        [theme.breakpoints.down('md')]: {
            flex: '0 0 60px',
            fontSize: '0.6rem',
        },
    },
}));

const DashboardTablePanel = ({ tableData = [], onUsernameClick, onUserUpdate, selectedBroker, selectedAdmin, isBrokerView, isAdminView }) => {
    const { user } = useAuth(); // Get the authenticated user
    // Initialize state from localStorage or default to true (Share View)
    const [searchQuery, setSearchQuery] = useState('');
    const [isShareView, setIsShareView] = useState(
        localStorage.getItem('dashboardViewPreference')
            ? localStorage.getItem('dashboardViewPreference') === 'share'
            : true
    );
    const [isExpanded, setIsExpanded] = useState(false);
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    // Save preference to localStorage when view changes
    const handleViewChange = (checked) => {
        setIsShareView(checked);
        localStorage.setItem('dashboardViewPreference', checked ? 'share' : 'list');
    };

    // Sync localStorage if isShareView changes from elsewhere
    useEffect(() => {
        localStorage.setItem('dashboardViewPreference', isShareView ? 'share' : 'list');
    }, [isShareView]);    // Transform API data to the format expected by PLSummaryCard
    const transformedData = tableData.map(item => ({
        username: item?.trader?.username?.toUpperCase(),
        userReferenceName: item.trader ?
            `${item.trader.firstname?.toUpperCase() || ''} ${item.trader.lastname?.toUpperCase() || ''}`.trim() : '',
        adminGrossPL: formatCurrency(item.yourGrossPl || 0),
        adminActivePL: formatCurrency(item.yourActivePl || 0),
        adminBookedPL: formatCurrency(item.yourProfitLoss || 0),
        adminBrokerage: formatCurrency(item.yourBrokerage || 0),
        sAdminGrossPL: formatCurrency(item.superAdminGrossPl || 0),
        sAdminActivePL: formatCurrency(item.superAdminActivePl || 0),
        sAdminBookePL: formatCurrency(item.superAdminProfit || 0),
        sAdminBrokerage: formatCurrency(item.superAdminBrokerage || 0),
        brokerGrossPL: formatCurrency(item.grossPl || 0),
        brokerActivePL: formatCurrency(item.activeProfitLoss || 0),
        brokerBookedPL: formatCurrency(item.totalProfit || 0),
        brokerBrokerage: formatCurrency(item.totalBrokerage || 0),
        refundBrokerage: formatCurrency(item.refundBrokerage || 0),
        trader: item.trader, // Include the full trader object with _id
        // Include balance data for AddBalanceModal
        balance: item.balance || 0,
        activeProfitLoss: item.activeProfitLoss || 0,
        totalProfit: item.totalProfit || 0,
        totalBrokerage: item.totalBrokerage || 0,
        blockedMargin: item.blockedMargin || 0
    }));    // Transform API data to the format expected by BrokerDataTable
    const formatTableDataForBrokerTable = (filteredTableData = tableData) => {
        if (!filteredTableData || filteredTableData.length === 0) return [];

        return filteredTableData.map(item => ({
            username: item.trader.username.toUpperCase() || '',
            grossPL: formatCurrency(item.grossPl || 0),
            activePL: formatCurrency(item.activeProfitLoss || 0),
            bookedPL: formatCurrency(item.totalProfit || 0),
            brk: formatCurrency(item.totalBrokerage || 0),
            refunBrk: formatCurrency(item.refundBrokerage || 0),
            balance: formatCurrency(item.balance || 0),
            trader: item.trader, // Include the full trader object with _id
            // Preserve original API data for modal usage
            originalData: item // Keep the complete original item for use in modals
        }));
    };    // Filter logic for searching users - apply to original tableData first
    const filteredTableData = tableData.filter(item => {
        // First filter: only show items with hasBalance === true
        if (item?.hasBalance !== true) return false;

        // Second filter: search query
        if (!searchQuery) return true;
        const searchLower = searchQuery.toLowerCase();
        const username = item?.trader?.username?.toLowerCase() || '';
        const userReferenceName = item.trader ?
            `${item.trader.firstname?.toLowerCase() || ''} ${item.trader.lastname?.toLowerCase() || ''}`.trim() : '';
        return username.includes(searchLower) || userReferenceName.includes(searchLower);
    });
    // Transform filtered API data to the format expected by PLSummaryCard
    const filteredTransformedData = filteredTableData.map(item => ({
        username: item?.trader?.username?.toUpperCase(),
        userReferenceName: item.trader ?
            `${item.trader.firstname?.toUpperCase() || ''} ${item.trader.lastname?.toUpperCase() || ''}`.trim() : '',
        adminGrossPL: formatCurrency(item.yourGrossPl || 0),
        adminActivePL: formatCurrency(item.yourActivePl || 0),
        adminBookedPL: formatCurrency(item.yourProfitLoss || 0),
        adminBrokerage: formatCurrency(item.yourBrokerage || 0),
        sAdminGrossPL: formatCurrency(item.superAdminGrossPl || 0),
        sAdminActivePL: formatCurrency(item.superAdminActivePl || 0),
        sAdminBookePL: formatCurrency(item.superAdminProfit || 0),
        sAdminBrokerage: formatCurrency(item.superAdminBrokerage || 0),
        brokerGrossPL: formatCurrency(item.grossPl || 0),
        brokerActivePL: formatCurrency(item.activeProfitLoss || 0),
        brokerBookedPL: formatCurrency(item.totalProfit || 0),
        brokerBrokerage: formatCurrency(item.totalBrokerage || 0),
        refundBrokerage: formatCurrency(item.refundBrokerage || 0),
        trader: item.trader, // Include the full trader object with _id
        // Include balance data for AddBalanceModal
        balance: item.balance || 0,
        activeProfitLoss: item.activeProfitLoss || 0,
        totalProfit: item.totalProfit || 0,
        totalBrokerage: item.totalBrokerage || 0,
        blockedMargin: item.blockedMargin || 0
    }));

    // Calculate totals for Share View
    const calculateShareViewTotals = () => {
        return filteredTransformedData.reduce((acc, item) => {
            // Parse numeric values from formatted strings
            const parseFormattedValue = (value) => {
                if (typeof value === 'string') {
                    // Remove currency symbol and commas, then parse
                    const cleaned = value.replace(/[₹,]/g, '').trim();
                    return parseFloat(cleaned) || 0;
                }
                return parseFloat(value) || 0;
            };

            acc.adminGrossPL += parseFormattedValue(item.adminGrossPL);
            acc.adminActivePL += parseFormattedValue(item.adminActivePL);
            acc.adminBookedPL += parseFormattedValue(item.adminBookedPL);
            acc.adminBrokerage += parseFormattedValue(item.adminBrokerage);

            acc.sAdminGrossPL += parseFormattedValue(item.sAdminGrossPL);
            acc.sAdminActivePL += parseFormattedValue(item.sAdminActivePL);
            acc.sAdminBookePL += parseFormattedValue(item.sAdminBookePL);
            acc.sAdminBrokerage += parseFormattedValue(item.sAdminBrokerage);

            acc.brokerGrossPL += parseFormattedValue(item.brokerGrossPL);
            acc.brokerActivePL += parseFormattedValue(item.brokerActivePL);
            acc.brokerBookedPL += parseFormattedValue(item.brokerBookedPL);
            acc.brokerBrokerage += parseFormattedValue(item.brokerBrokerage);
            acc.refundBrokerage += parseFormattedValue(item.refundBrokerage);

            return acc;
        }, {
            adminGrossPL: 0,
            adminActivePL: 0,
            adminBookedPL: 0,
            adminBrokerage: 0,
            sAdminGrossPL: 0,
            sAdminActivePL: 0,
            sAdminBookePL: 0,
            sAdminBrokerage: 0,
            brokerGrossPL: 0,
            brokerActivePL: 0,
            brokerBookedPL: 0,
            brokerBrokerage: 0,
            refundBrokerage: 0
        });
    };

    const shareViewTotals = calculateShareViewTotals();

    return (
        <UnifiedPanel>
            <FilterContainer>
                <Tooltip title={isShareView ? "Share View" : "List View"}>
                    <StyledFormControlLabel
                        control={
                            <Switch
                                size="small"
                                checked={isShareView}
                                onChange={(e) => handleViewChange(e.target.checked)}
                                icon={<ViewListIcon />}
                                checkedIcon={<ShareIcon />}
                            />
                        }
                    />
                </Tooltip>
                <SearchTextField
                    placeholder="Search User.."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    variant="outlined"
                    size="small"
                    sx={{ flex: 1 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ fontSize: 20, color: 'text.disabled' }} />
                            </InputAdornment>
                        ),
                    }}
                />
                {/* <Tooltip title="Filters">
                    <StyledIconButton
                        onClick={() => setIsFilterOpen(!isFilterOpen)}
                        size="small"
                    >
                        <FilterListIcon />
                    </StyledIconButton>
                </Tooltip> */}
                <Tooltip title={isExpanded ? "Collapse All" : "Expand All"}>
                    <StyledIconButton
                        onClick={() => setIsExpanded(!isExpanded)}
                        size="small"
                    >
                        {isExpanded ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                    </StyledIconButton>
                </Tooltip>
            </FilterContainer>
            {isShareView ? (
                <ScrollableContainer itemCount={filteredTransformedData.length}>
                    {/* Metrics Header Row - only shown on desktop */}
                    <MetricsHeaderRow sx={{ display: { xs: 'none', lg: 'flex' } }}>
                        <Box className="user-section">User</Box>
                        <Box className="metrics-section">
                            <Box className="metric-header">{isBrokerView ? 'BROKER(%)' : 'ADMIN(%)'}</Box>
                            <Box className="metric-header">{isBrokerView ? 'ADMIN(%)' : 'S-ADMIN(%)'}</Box>
                            <Box className="metric-header">CLIENT GROSS</Box>
                        </Box>
                        <Box className="actions-section">ACTIONS</Box>
                    </MetricsHeaderRow>



                    <Stack
                        spacing={1}
                        sx={{
                            width: '100%',
                            height: 'fit-content',
                            px: 0,
                            pb: 0,
                            '& > .MuiPaper-root': {
                                flexShrink: 0,
                                background: '#ffffff',
                                boxShadow: theme => `0 2px 8px ${alpha(theme.palette.grey[500], 0.08)}`,
                                borderRadius: theme => theme.spacing(1.5),
                                transition: 'all 0.2s ease-in-out',
                                border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
                                position: 'relative',
                                '&:hover': {
                                    boxShadow: theme => `0 4px 12px ${alpha(theme.palette.grey[500], 0.12)}`,
                                }
                            }
                        }}
                    >
                        {filteredTransformedData.length > 0 ? (filteredTransformedData.map((data, index) => (
                            <PLSummaryCard
                                key={index}
                                username={data.username}
                                userReferenceName={data.userReferenceName}
                                adminGrossPL={data.adminGrossPL}
                                adminActivePL={data.adminActivePL}
                                adminBookedPL={data.adminBookedPL}
                                adminBrokerage={data.adminBrokerage}
                                sAdminGrossPL={data.sAdminGrossPL}
                                sAdminActivePL={data.sAdminActivePL}
                                sAdminBookePL={data.sAdminBookePL}
                                sAdminBrokerage={data.sAdminBrokerage}
                                brokerGrossPL={data.brokerGrossPL}
                                brokerActivePL={data.brokerActivePL}
                                brokerBookedPL={data.brokerBookedPL}
                                brokerBrokerage={data.brokerBrokerage}
                                refundBrokerage={data.refundBrokerage}
                                isBrokerView={isBrokerView}
                                isAdminView={isAdminView}
                                selectedBroker={selectedBroker}
                                selectedAdmin={selectedAdmin}
                                userData={{ 
                                    trader: data.trader,
                                    balance: data.balance,
                                    activeProfitLoss: data.activeProfitLoss,
                                    totalProfit: data.totalProfit,
                                    totalBrokerage: data.totalBrokerage,
                                    blockedMargin: data.blockedMargin
                                }} // Pass complete trader and balance data
                                onUserUpdate={onUserUpdate} // Pass the onUserUpdate callback
                                showDesktopMetrics={false} // Hide desktop metrics since we show them in header
                                onClick={() => {
                                    // Find the matching trader ID from the filtered table data
                                    const trader = filteredTableData.find(item =>
                                        item.trader?.username?.toUpperCase() === data.username
                                    );
                                    if (trader && trader.trader?._id && onUsernameClick && !isBrokerView && user?.role?.rolename !== 'broker') {
                                        onUsernameClick(trader.trader._id);
                                    }
                                }}
                                defaultExpanded={isExpanded}
                            />
                        ))) : (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    height: '120px',
                                    width: '100%',
                                    bgcolor: 'background.paper',
                                    borderRadius: theme => theme.spacing(1.5),
                                    boxShadow: theme => `0 2px 8px ${alpha(theme.palette.grey[500], 0.08)}`,
                                    border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
                                    color: 'text.secondary',
                                    fontSize: '0.875rem',
                                }}
                            >
                                No data available
                            </Box>)}
                    </Stack>
                    {/* Totals Row - show on mobile for compact view, desktop for full view */}
                    {filteredTransformedData.length > 0 && (
                        <MetricsTotalsRow sx={{ display: 'flex' }}>
                            <Box className="user-section">TOTAL</Box>
                            <Box className="metrics-section">
                                <Box
                                    className="metric-total"
                                    sx={{
                                        color: shareViewTotals.adminGrossPL >= 0 ? '#1b5e20' : '#c62828'
                                    }}
                                >
                                    {formatCurrency(isBrokerView ? shareViewTotals.adminGrossPL : shareViewTotals.adminGrossPL)}
                                </Box>
                                <Box
                                    className="metric-total"
                                    sx={{
                                        color: shareViewTotals.sAdminGrossPL >= 0 ? '#1b5e20' : '#c62828'
                                    }}
                                >
                                    {formatCurrency(isBrokerView ? shareViewTotals.sAdminGrossPL : shareViewTotals.sAdminGrossPL)}
                                </Box>
                                <Box
                                    className="metric-total"
                                    sx={{
                                        color: shareViewTotals.brokerGrossPL >= 0 ? '#1b5e20' : '#c62828'
                                    }}
                                >
                                    {formatCurrency(shareViewTotals.brokerGrossPL)}
                                </Box>
                            </Box>
                            <Box className="actions-section">-</Box>
                        </MetricsTotalsRow>
                    )}
                </ScrollableContainer>) : (<Box
                    sx={(theme) => ({
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        height: 'fit-content', // Changed from fixed height to fit-content
                        overflowY: 'visible', // Allow vertical content to expand
                        overflowX: 'auto', // Allow horizontal scrolling
                        [theme.breakpoints.down('md')]: {
                            height: 'fit-content', // Changed from fixed height to fit-content
                        }
                    })}
                >
                    <BrokerDataTable
                        data={formatTableDataForBrokerTable(filteredTableData)}
                        formatCurrency={formatCurrency}
                        onSettingsClick={null} // Remove the onSettingsClick prop to allow internal navigation logic
                        onUsernameClick={(username) => {
                            // Find the matching trader ID from the filtered table data
                            const trader = filteredTableData.find(item =>
                                item.trader?.username?.toUpperCase() === username
                            );
                            if (trader && trader.trader?._id && onUsernameClick) {
                                onUsernameClick(trader.trader._id);
                            }
                        }}
                        onUserUpdate={onUserUpdate}
                        maxHeight="none"
                        selectedBroker={selectedBroker?.username?.toUpperCase()}
                        selectedAdmin={selectedAdmin}
                        isBrokerView={isBrokerView}
                        isAdminView={isAdminView}
                        isShare={isShareView} // Enable sharing view in broker mode too
                    />
                </Box>
            )}
        </UnifiedPanel>
    );
};

export default DashboardTablePanel;
